import { useState, useEffect, useCallback, useMemo, ReactElement, Fragment } from 'react'
import {
  Box,
  Typography,
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Chip,
  CircularProgress,
  Skeleton,
  Drawer,
  TextField,
  MenuItem,
  Link,
  useTheme,
  useMediaQuery,
} from '@mui/material'
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Business as BusinessIcon,
  Star as StarIcon,
  EmojiEvents as CompetitorIcon,
  AutoAwesome as WildcardIcon,
  WarningAmber as WarningIcon,
} from '@mui/icons-material'
import {
  Client,
  DreamJob,
  TargetCompany,
  EmployerQuality,
  TargetCompanyCategory,
  clientsApi,
  contactsApi,
  Contact,
} from '../../services/api'
import { findSimilarCompany } from '../../utils/companyName'
import { useNotification } from '../../hooks/useNotification'
import WarmContactsCell, { warmContactsAt, contactDisplayName } from './WarmContactsCell'
import { getEnhancedTableStyles } from '../../utils/tableStyles'
import { getCompactTableSx } from '../../utils/compactTableStyles'
import { ExpanderField, ExpanderHeaderCell, ExpanderRow, ExpanderToggleCell } from '../common/table/RowExpander'
import SortableTableHeader, { SortOrder } from '../common/table/SortableTableHeader'
import FilterBar, { FilterBarFilter } from '../common/table/FilterBar'
import { useTablePagination } from '../common/table/useTablePagination'
import TablePaginationBar from '../common/table/TablePaginationBar'
import DialogShell, { ShellBody, ShellFooter, ShellHeader } from '../common/DialogShell'
import PanelSection from '../common/PanelSection'

type TargetCompanyTier = TargetCompany['tier']

interface TargetCompaniesTabProps {
  clientId?: string
  canEdit?: boolean
  // Show the in-tab "Add Company" buttons (header + empty state). Defaults to
  // true (the portal relies on them). The staff Job Search page sets this false
  // and adds via its header "Add" menu instead — see autoOpenAdd below.
  showAddButton?: boolean
  // One-shot trigger from a parent "Add" menu: when it flips true we open the
  // Add dialog and call onAutoAddHandled so the parent can clear it (keeping it
  // a one-shot that won't re-fire when this tab remounts).
  autoOpenAdd?: boolean
  onAutoAddHandled?: () => void
}

interface TargetCompanyFormState {
  name: string
  tier: TargetCompanyTier
  employerQuality: EmployerQuality
  listReason: TargetCompanyCategory
  location: string
  rationale: string
}

const EMPTY_FORM: TargetCompanyFormState = {
  name: '',
  tier: 'wildcard',
  employerQuality: 'fair',
  listReason: 'wildcard',
  location: '',
  rationale: '',
}

const TIER_OPTIONS: { value: TargetCompanyTier; label: string }[] = [
  { value: 'goal', label: 'Goal' },
  { value: 'backup', label: 'Backup' },
  { value: 'wildcard', label: 'Wild Card' },
]

const QUALITY_OPTIONS: { value: EmployerQuality; label: string }[] = [
  { value: 'good', label: 'Good' },
  { value: 'fair', label: 'Fair' },
  { value: 'poor', label: 'Poor' },
]

// "Why listed" — a different question from tier (see TargetCompany.listReason
// in services/api.ts). Deliberately labeled "Why Listed" everywhere in this
// file, never "Category", so it's never confused with Tier.
const LIST_REASON_OPTIONS: { value: TargetCompanyCategory; label: string }[] = [
  { value: 'north_star', label: 'North Star' },
  { value: 'competitor', label: 'Competitor' },
  { value: 'wildcard', label: 'Wild Card' },
]

const TIER_META: Record<TargetCompanyTier, { label: string; color: 'success' | 'warning' | 'default' }> = {
  goal: { label: 'Goal', color: 'success' },
  backup: { label: 'Backup', color: 'warning' },
  wildcard: { label: 'Wild Card', color: 'default' },
}

// Sort rank so Tier orders goal → backup → wildcard (asc), matching the
// options list above, rather than alphabetically.
const TIER_RANK: Record<TargetCompanyTier, number> = { goal: 0, backup: 1, wildcard: 2 }

const QUALITY_META: Record<
  EmployerQuality,
  { label: string; color: 'success' | 'warning' | 'error' }
> = {
  good: { label: 'Good', color: 'success' },
  fair: { label: 'Fair', color: 'warning' },
  poor: { label: 'Poor', color: 'error' },
}

// Sort rank so the Employer Quality column orders good → fair → poor (asc)
// rather than alphabetically (which would put fair before good).
const QUALITY_RANK: Record<EmployerQuality, number> = { good: 0, fair: 1, poor: 2 }

const LIST_REASON_META: Record<
  TargetCompanyCategory,
  { label: string; color: 'primary' | 'secondary' | 'info'; icon: ReactElement }
> = {
  north_star: { label: 'North Star', color: 'primary', icon: <StarIcon fontSize="inherit" /> },
  competitor: { label: 'Competitor', color: 'secondary', icon: <CompetitorIcon fontSize="inherit" /> },
  wildcard: { label: 'Wild Card', color: 'info', icon: <WildcardIcon fontSize="inherit" /> },
}

const LIST_REASON_RANK: Record<TargetCompanyCategory, number> = { north_star: 0, competitor: 1, wildcard: 2 }

// A labeled Goal/Backup/Wildcard chip for HOW MUCH the client wants this
// company. Kept visually and textually distinct from ListReasonBadge (WHY
// it's on the list) even though both vocabularies include "wildcard".
function TierChip({ tier, variant }: { tier: TargetCompanyTier; variant?: 'filled' | 'outlined' }) {
  const meta = TIER_META[tier] || TIER_META.wildcard
  return <Chip label={meta.label} color={meta.color} size="small" variant={variant} sx={{ fontWeight: 600 }} />
}

// A labeled Good/Fair/Poor chip — deliberately distinct from the numeric
// fit-score badge used on Opportunities (uses a word, not a number).
function QualityChip({ quality, variant }: { quality: EmployerQuality; variant?: 'filled' | 'outlined' }) {
  const meta = QUALITY_META[quality] || QUALITY_META.fair
  return <Chip label={meta.label} color={meta.color} size="small" variant={variant} sx={{ fontWeight: 600 }} />
}

// The table cell and the tablet row expander both show this.
function renderEmployerQuality(company: TargetCompany) {
  return company.employerQuality ? (
    <QualityChip quality={company.employerQuality} />
  ) : (
    <Typography variant="body2" color="text.secondary">
      Not rated
    </Typography>
  )
}

function ListReasonBadge({ reason }: { reason: TargetCompanyCategory }) {
  const meta = LIST_REASON_META[reason] || LIST_REASON_META.wildcard
  return (
    <Chip
      icon={meta.icon}
      label={meta.label}
      color={meta.color}
      size="small"
      variant="outlined"
      sx={{ fontWeight: 600 }}
    />
  )
}

// clientsApi.saveDreamJob (DreamJobForm's own save path) writes the WHOLE
// dream job document — targetCompanies is one field on it. Every write from
// this tab must round-trip the client's other dream-job fields untouched, or
// editing a company here would blank out their target role, salary target,
// priorities, etc. Falls back to the same defaults DreamJobForm uses when the
// client has no dream job yet (e.g. adding a company before the intake form
// has ever been saved).
function buildDreamJobPayload(
  existing: DreamJob | undefined,
  targetCompanies: TargetCompany[]
): Omit<DreamJob, 'completedAt' | 'completedBy'> {
  return {
    targetRole: existing?.targetRole,
    targetIndustry: existing?.targetIndustry,
    targetCompanies,
    targetJobLinks: existing?.targetJobLinks || [],
    salaryTarget: existing?.salaryTarget,
    salaryMinimum: existing?.salaryMinimum,
    benefitsPriorities: existing?.benefitsPriorities || [],
    workStyle: existing?.workStyle,
    locationPreferences: existing?.locationPreferences || [],
    willingToRelocate: existing?.willingToRelocate || false,
    priorities: existing?.priorities || [],
    dealBreakers: existing?.dealBreakers || [],
    targetStartDate: existing?.targetStartDate,
    urgency: existing?.urgency || 'moderate',
    additionalRequirements: existing?.additionalRequirements,
  }
}

export default function TargetCompaniesTab({
  clientId,
  canEdit = false,
  showAddButton = true,
  autoOpenAdd = false,
  onAutoAddHandled,
}: TargetCompaniesTabProps) {
  const [client, setClient] = useState<Client | null>(null)
  const [contacts, setContacts] = useState<Contact[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Drawer state
  const [selected, setSelected] = useState<TargetCompany | null>(null)
  // MWA-254 Defect 3 — editable detail drawer (staff only via canEdit).
  const [editing, setEditing] = useState(false)
  const [savingEdit, setSavingEdit] = useState(false)
  const [editForm, setEditForm] = useState<{
    tier: TargetCompanyTier
    listReason: TargetCompanyCategory | ''
    employerQuality: EmployerQuality
    location: string
    rationale: string
  }>({ tier: 'wildcard', listReason: '', employerQuality: 'fair', location: '', rationale: '' })

  // Add dialog state
  const [addOpen, setAddOpen] = useState(false)
  const [form, setForm] = useState<TargetCompanyFormState>(EMPTY_FORM)
  const [submitting, setSubmitting] = useState(false)

  // Delete confirmation — entries have no id once folded into the dream job
  // doc, so name is the stable identity (also how DreamJobForm's own mini
  // editor and the dedupe hint below key entries).
  const [deletingName, setDeletingName] = useState<string | null>(null)
  // The panel Delete button swaps the footer to an inline confirm.
  const [confirmingDelete, setConfirmingDelete] = useState(false)

  const { showSuccess, showError } = useNotification()

  const theme = useTheme()
  const tableStyles = getEnhancedTableStyles(theme)
  // 768-1199: Company, Tier, Why Listed and Warm Contacts at fixed widths, with
  // Employer Quality and Location in a row expander, so the table fits beside
  // the 260px rail without scrolling sideways. Phones keep the full table.
  const isCompact = useMediaQuery(theme.breakpoints.between(768, 1200))
  const compactWidth = (width: number | string | Record<string, number | string>) =>
    isCompact ? { width } : undefined
  const [expandedKey, setExpandedKey] = useState<string | null>(null)

  // CrowCase-style sort + filter
  const [orderBy, setOrderBy] = useState<string>('')
  const [order, setOrder] = useState<SortOrder>('asc')
  const [tcFilters, setTcFilters] = useState<{ tier: string[]; quality: string[]; listReason: string[] }>({
    tier: [],
    quality: [],
    listReason: [],
  })

  const handleSort = (columnId: string) => {
    if (orderBy === columnId) setOrder((o) => (o === 'asc' ? 'desc' : 'asc'))
    else {
      setOrderBy(columnId)
      setOrder('asc')
    }
  }

  const tcFilterConfig: FilterBarFilter[] = [
    { name: 'tier', label: 'Tier', options: TIER_OPTIONS, value: tcFilters.tier },
    { name: 'quality', label: 'Employer Quality', options: QUALITY_OPTIONS, value: tcFilters.quality },
    { name: 'listReason', label: 'Why Listed', options: LIST_REASON_OPTIONS, value: tcFilters.listReason },
  ]
  const handleTcFilterChange = (name: string, value: string[]) =>
    setTcFilters((prev) => ({ ...prev, [name]: value }))
  const clearTcFilters = () => setTcFilters({ tier: [], quality: [], listReason: [] })

  // clients.dreamJob.targetCompanies is the single home for this data now
  // (folded in from the retired standalone target-companies table). A legacy
  // entry can be a bare string with no tier — DreamJobForm and NetworkingTab
  // already tolerate this on read; mirror that here rather than crashing on
  // old data.
  const companies = useMemo<TargetCompany[]>(() => {
    const raw = (client?.dreamJob?.targetCompanies || []) as unknown[]
    return raw.map((c) =>
      typeof c === 'string' ? { name: c, tier: 'wildcard' as const } : (c as TargetCompany)
    )
  }, [client?.dreamJob?.targetCompanies])

  const displayCompanies = useMemo(() => {
    const filtered = companies.filter((c) => {
      if (tcFilters.tier.length > 0 && !tcFilters.tier.includes(c.tier)) return false
      if (tcFilters.quality.length > 0 && (!c.employerQuality || !tcFilters.quality.includes(c.employerQuality)))
        return false
      if (tcFilters.listReason.length > 0 && (!c.listReason || !tcFilters.listReason.includes(c.listReason)))
        return false
      return true
    })
    if (!orderBy) return filtered
    return filtered.slice().sort((a, b) => {
      let aVal: string | number
      let bVal: string | number
      switch (orderBy) {
        case 'name':
          aVal = a.name || ''
          bVal = b.name || ''
          break
        case 'tier':
          aVal = TIER_RANK[a.tier] ?? 99
          bVal = TIER_RANK[b.tier] ?? 99
          break
        case 'employerQuality':
          aVal = a.employerQuality ? QUALITY_RANK[a.employerQuality] : 99
          bVal = b.employerQuality ? QUALITY_RANK[b.employerQuality] : 99
          break
        case 'listReason':
          aVal = a.listReason ? LIST_REASON_RANK[a.listReason] : 99
          bVal = b.listReason ? LIST_REASON_RANK[b.listReason] : 99
          break
        case 'location':
          aVal = a.location || ''
          bVal = b.location || ''
          break
        default:
          return 0
      }
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return order === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      return order === 'asc'
        ? (aVal as number) - (bVal as number)
        : (bVal as number) - (aVal as number)
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, orderBy, order, tcFilters])

  // MWA-254 Defect 2 — client-side pagination. Reset to page 1 whenever the
  // sort/filter result set changes.
  const { paginated: pagedCompanies, ...pageCtl } = useTablePagination(
    displayCompanies,
    `${orderBy}|${order}|${tcFilters.tier.join(',')}|${tcFilters.quality.join(',')}|${tcFilters.listReason.join(',')}`
  )

  const loadClient = useCallback(async () => {
    if (!clientId) {
      setClient(null)
      return
    }
    setLoading(true)
    setError(null)
    try {
      const data = await clientsApi.get(clientId)
      setClient(data)
    } catch (err) {
      console.error('Failed to load target companies:', err)
      setError('Failed to load target companies')
    } finally {
      setLoading(false)
    }
  }, [clientId])

  useEffect(() => {
    loadClient()
  }, [loadClient])

  // MWA-237/254 — load the client's contacts so we can flag companies where the
  // client already has a warm contact (someone who could make an introduction).
  useEffect(() => {
    if (!clientId) {
      setContacts([])
      return
    }
    let cancelled = false
    contactsApi
      .list(clientId)
      .then((res) => {
        if (!cancelled) setContacts(Array.isArray(res) ? res : [])
      })
      .catch(() => {
        if (!cancelled) setContacts([])
      })
    return () => {
      cancelled = true
    }
  }, [clientId])

  const handleRowClick = (company: TargetCompany) => {
    setSelected(company)
  }

  const handleCloseDrawer = () => {
    setSelected(null)
    setEditing(false)
    setConfirmingDelete(false)
  }

  // MWA-254 Defect 3 — leave edit mode when the drawer switches to a different
  // company so a stale edit form never carries over to another record.
  useEffect(() => {
    setEditing(false)
    setConfirmingDelete(false)
  }, [selected?.name])

  // Seed the edit form from the selected company whenever we enter edit mode.
  useEffect(() => {
    if (editing && selected) {
      setEditForm({
        tier: selected.tier || 'wildcard',
        listReason: selected.listReason || '',
        employerQuality: selected.employerQuality || 'fair',
        location: selected.location || '',
        rationale: selected.rationale || '',
      })
    }
  }, [editing, selected])

  // Writes the full targetCompanies array through the same dream-job save
  // path DreamJobForm uses, and keeps local state in sync with what the
  // server actually persisted.
  const saveCompanies = async (updated: TargetCompany[]): Promise<TargetCompany[]> => {
    if (!clientId) throw new Error('No client selected')
    const payload = buildDreamJobPayload(client?.dreamJob, updated)
    const result = await clientsApi.saveDreamJob(clientId, payload)
    setClient((prev) => (prev ? { ...prev, dreamJob: result.dreamJob } : prev))
    return result.dreamJob.targetCompanies
  }

  const handleSaveEdit = async () => {
    if (!selected) return
    const changed: Partial<TargetCompany> = {}
    if (editForm.tier !== (selected.tier || 'wildcard')) changed.tier = editForm.tier
    // listReason is optional, so its unset state is '' — NOT 'wildcard'. A
    // company nobody gave a reason has no reason; defaulting the comparison to
    // 'wildcard' would show "Wild card" in the select for every such company
    // and report a reason the data does not carry.
    if (editForm.listReason !== (selected.listReason || ''))
      changed.listReason = editForm.listReason || undefined
    if (editForm.employerQuality !== (selected.employerQuality || 'fair'))
      changed.employerQuality = editForm.employerQuality
    if (editForm.location !== (selected.location || '')) changed.location = editForm.location
    if (editForm.rationale !== (selected.rationale || '')) changed.rationale = editForm.rationale

    // Nothing changed — just exit edit mode without a round-trip.
    if (Object.keys(changed).length === 0) {
      setEditing(false)
      return
    }

    setSavingEdit(true)
    try {
      const updatedCompanies = companies.map((c) => {
        if (c.name !== selected.name) return c
        const next = { ...c, ...changed }
        // The API validates listReason against a closed list, so clearing it
        // has to drop the key rather than send an empty string, which would be
        // rejected as an invalid value.
        if (!next.listReason) delete next.listReason
        return next
      })
      const saved = await saveCompanies(updatedCompanies)
      setSelected(saved.find((c) => c.name === selected.name) || null)
      showSuccess('Company updated')
      setEditing(false)
    } catch (err) {
      console.error('Failed to update target company:', err)
      showError('Failed to update company')
    } finally {
      setSavingEdit(false)
    }
  }

  const handleOpenAdd = () => {
    setForm(EMPTY_FORM)
    setAddOpen(true)
  }

  // Open the Add dialog when a parent "Add" menu requests it, then acknowledge
  // so the trigger is one-shot (won't re-fire on remount).
  useEffect(() => {
    if (autoOpenAdd) {
      handleOpenAdd()
      onAutoAddHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenAdd])

  const handleSubmitAdd = async () => {
    if (!clientId) {
      showError('No client selected')
      return
    }
    if (!form.name.trim()) {
      showError('Company name is required')
      return
    }
    setSubmitting(true)
    try {
      const newCompany: TargetCompany = {
        name: form.name.trim(),
        tier: form.tier,
        employerQuality: form.employerQuality,
        listReason: form.listReason,
        location: form.location.trim(),
        rationale: form.rationale.trim(),
        source: 'manual',
      }
      await saveCompanies([...companies, newCompany])
      showSuccess('Company added')
      setAddOpen(false)
      setForm(EMPTY_FORM)
    } catch (err) {
      console.error('Failed to add target company:', err)
      showError('Failed to add company')
    } finally {
      setSubmitting(false)
    }
  }

  // Non-blocking "did you mean?" hint: as the user types a company name, look
  // for an already-present company whose normalized name matches or is very
  // similar. Never blocks submission — purely a guard against accidental dupes.
  const didYouMean = useMemo(() => {
    const typed = form.name.trim()
    if (typed.length < 2) return null
    return findSimilarCompany(
      typed,
      companies.map((c) => c.name)
    )
  }, [form.name, companies])

  const handleDelete = async (company: TargetCompany) => {
    setDeletingName(company.name)
    try {
      await saveCompanies(companies.filter((c) => c.name !== company.name))
      if (selected?.name === company.name) handleCloseDrawer()
      showSuccess('Company removed')
    } catch (err) {
      console.error('Failed to remove target company:', err)
      showError('Failed to remove company')
    } finally {
      setDeletingName(null)
      setConfirmingDelete(false)
    }
  }

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 2,
          gap: 2,
          flexWrap: 'wrap',
        }}
      >
        <Box>
          <Typography variant="h6">Target Companies</Typography>
          <Typography variant="body2" color="text.secondary">
            Employers worth pursuing, tiered by how much the client wants them and rated by
            employer quality.
          </Typography>
        </Box>
        {canEdit && (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {showAddButton && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpenAdd} disabled={!clientId}>
                Add Company
              </Button>
            )}
          </Box>
        )}
      </Box>

      {loading && (
        <Box>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <Skeleton variant="rounded" width={160} height={36} />
            <Skeleton variant="rounded" width={160} height={36} />
            <Skeleton variant="rounded" width={160} height={36} />
          </Box>
          <Skeleton variant="rounded" height={48} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={52} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={52} sx={{ mb: 1 }} />
          <Skeleton variant="rounded" height={52} />
        </Box>
      )}

      {!loading && error && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Button onClick={loadClient} sx={{ mt: 1 }} size="small">
            Retry
          </Button>
        </Box>
      )}

      {!loading && !error && companies.length === 0 && (
        <Paper variant="outlined" sx={{ py: 6, px: 2, textAlign: 'center' }}>
          <BusinessIcon color="disabled" sx={{ fontSize: 48, mb: 1 }} />
          <Typography variant="subtitle1" color="text.secondary">
            No target companies yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {canEdit
              ? 'Add companies worth pursuing to start building the target list.'
              : 'Target companies will appear here once added.'}
          </Typography>
          {canEdit && showAddButton && (
            <Button variant="outlined" startIcon={<AddIcon />} onClick={handleOpenAdd} disabled={!clientId}>
              Add Company
            </Button>
          )}
        </Paper>
      )}

      {!loading && !error && companies.length > 0 && (
        <>
          <FilterBar
            filters={tcFilterConfig}
            onChange={(name, value) => handleTcFilterChange(name, value as string[])}
            onClearAll={clearTcFilters}
          />
          {displayCompanies.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                No companies match the current filters.
              </Typography>
            </Box>
          ) : (
            <Paper variant="outlined" sx={tableStyles.tableContainer}>
              <Box sx={{ overflow: 'auto', maxHeight: '70vh' }}>
                <Table stickyHeader sx={{ ...tableStyles.table, ...(isCompact ? getCompactTableSx(theme) : undefined) }}>
                  <TableHead>
                    <TableRow>
                      {/* Company has no width at tablet: it takes what the others leave. */}
                      <SortableTableHeader id="name" label="Company" orderBy={orderBy} order={order} onSort={handleSort} />
                      <SortableTableHeader id="tier" label="Tier" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth({ xs: 96, md: 108 })} />
                      <SortableTableHeader id="listReason" label="Why Listed" orderBy={orderBy} order={order} onSort={handleSort} sx={compactWidth({ xs: 120, md: 132 })} />
                      {!isCompact && (
                        <>
                          <SortableTableHeader id="employerQuality" label="Employer Quality" orderBy={orderBy} order={order} onSort={handleSort} />
                          <SortableTableHeader id="location" label="Location" orderBy={orderBy} order={order} onSort={handleSort} />
                        </>
                      )}
                      <TableCell sx={compactWidth({ xs: '22%', md: 160 })}>Warm Contacts</TableCell>
                      {isCompact && <ExpanderHeaderCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pagedCompanies.map((company, idx) => {
                      const rowKey = `${company.name}-${idx}`
                      const open = expandedKey === rowKey
                      return (
                <Fragment key={rowKey}>
                <TableRow
                  hover
                  onClick={() => handleRowClick(company)}
                  sx={{ cursor: 'pointer' }}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {company.name}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <TierChip tier={company.tier} />
                  </TableCell>
                  <TableCell>
                    {company.listReason ? (
                      <ListReasonBadge reason={company.listReason} />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  {!isCompact && (
                    <>
                      <TableCell>{renderEmployerQuality(company)}</TableCell>
                      <TableCell sx={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {company.location || '—'}
                      </TableCell>
                    </>
                  )}
                  <TableCell>
                    <WarmContactsCell contacts={contacts} company={company.name} />
                  </TableCell>
                  {isCompact && (
                    <ExpanderToggleCell
                      open={open}
                      onToggle={() => setExpandedKey(open ? null : rowKey)}
                      subject={company.name}
                    />
                  )}
                </TableRow>
                {isCompact && (
                  <ExpanderRow open={open} colSpan={5}>
                    <ExpanderField label="Employer Quality">{renderEmployerQuality(company)}</ExpanderField>
                    <ExpanderField label="Location">
                      <Typography variant="body2">{company.location || '—'}</Typography>
                    </ExpanderField>
                  </ExpanderRow>
                )}
                </Fragment>
                      )
                    })}
                  </TableBody>
                </Table>
              </Box>
              <TablePaginationBar
                count={pageCtl.count}
                page={pageCtl.page}
                rowsPerPage={pageCtl.rowsPerPage}
                setPage={pageCtl.setPage}
                setRowsPerPage={pageCtl.setRowsPerPage}
              />
            </Paper>
          )}
        </>
      )}

      {/* Company detail drawer */}
      <Drawer
        anchor="right"
        open={!!selected}
        onClose={handleCloseDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 440 },
            maxWidth: '100vw',
            // Sit below the fixed 64px AppBar so the drawer header + close
            // button aren't clipped by the top nav. On a phone the panel is a
            // full-screen sheet instead.
            mt: { xs: 0, sm: '64px' },
            height: { xs: '100%', sm: 'calc(100% - 64px)' },
            backgroundColor: 'background.paper',
            color: 'text.primary',
          },
        }}
      >
        {selected && (
          <>
            <ShellHeader
              title={selected.name}
              subtitle="Target company"
              onClose={handleCloseDrawer}
              closeLabel="Close company details"
            />

            <ShellBody>
              {/* Tier and Why Listed answer different questions and both use
                  the word "wildcard" — always label them separately so a
                  reader never conflates the two. */}
              <PanelSection first>
                {editing ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <TextField
                      select
                      label="Tier"
                      value={editForm.tier}
                      onChange={(e) => setEditForm((f) => ({ ...f, tier: e.target.value as TargetCompanyTier }))}
                      size="small"
                      fullWidth
                      helperText="How much the client wants this company."
                    >
                      {TIER_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      label="Why Listed"
                      value={editForm.listReason}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, listReason: e.target.value as TargetCompanyCategory }))
                      }
                      size="small"
                      fullWidth
                      helperText="Why this company is on the list. Optional."
                    >
                      {/* The field is optional, so it has to be possible to get
                          back to unset — without this, picking a reason by mistake
                          could never be undone. */}
                      <MenuItem value="">
                        <em>Not specified</em>
                      </MenuItem>
                      {LIST_REASON_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      select
                      label="Employer Quality"
                      value={editForm.employerQuality}
                      onChange={(e) =>
                        setEditForm((f) => ({ ...f, employerQuality: e.target.value as EmployerQuality }))
                      }
                      size="small"
                      fullWidth
                    >
                      {QUALITY_OPTIONS.map((opt) => (
                        <MenuItem key={opt.value} value={opt.value}>
                          {opt.label}
                        </MenuItem>
                      ))}
                    </TextField>

                    <TextField
                      label="Location"
                      value={editForm.location}
                      onChange={(e) => setEditForm((f) => ({ ...f, location: e.target.value }))}
                      size="small"
                      fullWidth
                    />
                  </Box>
                ) : (
                  <Box
                    component="dl"
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: '1fr 1fr',
                      columnGap: 3,
                      rowGap: 2.5,
                      m: 0,
                      '& dt': { typography: 'caption', fontWeight: 600, color: 'text.secondary', mb: 0.5 },
                      '& dd': { m: 0, minWidth: 0, overflowWrap: 'anywhere' },
                    }}
                  >
                    <Box>
                      <dt>Tier</dt>
                      <dd>
                        <TierChip tier={selected.tier} variant="outlined" />
                      </dd>
                    </Box>
                    <Box>
                      <dt>Why Listed</dt>
                      <dd>
                        {selected.listReason ? (
                          <ListReasonBadge reason={selected.listReason} />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not set
                          </Typography>
                        )}
                      </dd>
                    </Box>
                    <Box>
                      <dt>Employer Quality</dt>
                      <dd>
                        {selected.employerQuality ? (
                          <QualityChip quality={selected.employerQuality} variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            Not rated
                          </Typography>
                        )}
                      </dd>
                    </Box>
                    <Box>
                      <dt>Location</dt>
                      <dd>
                        <Typography variant="body2">{selected.location || '—'}</Typography>
                      </dd>
                    </Box>
                  </Box>
                )}
              </PanelSection>

              <PanelSection title="Warm contacts">
                {(() => {
                  const warm = warmContactsAt(contacts, selected.name)
                  if (warm.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        No one in this client&apos;s network here yet.
                      </Typography>
                    )
                  }
                  return (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {warm.map((c) => (
                        <Chip key={c.id} size="small" variant="outlined" color="success" label={contactDisplayName(c)} />
                      ))}
                    </Box>
                  )
                })()}
              </PanelSection>

              <PanelSection title="Why this employer quality">
                {editing ? (
                  <TextField
                    label="Why this employer quality"
                    value={editForm.rationale}
                    onChange={(e) => setEditForm((f) => ({ ...f, rationale: e.target.value }))}
                    size="small"
                    fullWidth
                    multiline
                    rows={5}
                    helperText="Why this company has this employer-quality rating (culture, stability, pay)."
                  />
                ) : (
                  <Paper
                    variant="outlined"
                    sx={{
                      px: 1.5,
                      py: 1.25,
                      borderColor: 'divider',
                      borderRadius: '8px',
                      backgroundColor: 'background.paper',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography variant="body2" color={selected.rationale ? 'text.primary' : 'text.secondary'}>
                      {selected.rationale ||
                        (selected.source === 'north_star'
                          ? 'Re-seed from North Star to generate the employer-quality rationale.'
                          : 'No rationale provided for this company.')}
                    </Typography>
                  </Paper>
                )}
                {selected.source === 'north_star' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Generated from the North Star — read-only.
                  </Typography>
                )}
              </PanelSection>
            </ShellBody>

            {canEdit &&
              (editing ? (
                <ShellFooter>
                  <Button variant="text" onClick={() => setEditing(false)} disabled={savingEdit}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={savingEdit ? <CircularProgress size={16} /> : undefined}
                    onClick={handleSaveEdit}
                    disabled={savingEdit}
                  >
                    {savingEdit ? 'Saving...' : 'Save'}
                  </Button>
                </ShellFooter>
              ) : confirmingDelete ? (
                <ShellFooter
                  start={
                    <Box
                      role="alert"
                      sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'error.dark', fontWeight: 500 }}
                    >
                      <WarningIcon fontSize="small" />
                      <Typography variant="body2" component="span" sx={{ fontWeight: 'inherit' }}>
                        {`Remove ${selected.name}? This cannot be undone.`}
                      </Typography>
                    </Box>
                  }
                >
                  <Button
                    variant="text"
                    onClick={() => setConfirmingDelete(false)}
                    disabled={deletingName === selected.name}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={() => handleDelete(selected)}
                    disabled={deletingName === selected.name}
                  >
                    Remove
                  </Button>
                </ShellFooter>
              ) : (
                <ShellFooter
                  start={
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => setConfirmingDelete(true)}
                      disabled={deletingName === selected.name}
                    >
                      Delete
                    </Button>
                  }
                >
                  {/* MWA-261 — North-Star-seeded rows are read-only: the AI payload
                      empowers downstream reverse recruiting and must not be overridden.
                      Only manually-added rows can be edited. */}
                  {selected.source !== 'north_star' && (
                    <Button variant="outlined" startIcon={<EditIcon />} onClick={() => setEditing(true)}>
                      Edit
                    </Button>
                  )}
                </ShellFooter>
              ))}
          </>
        )}
      </Drawer>

      {/* Add company dialog */}
      <DialogShell
        open={addOpen}
        onClose={() => setAddOpen(false)}
        title="Add Company"
        maxWidth="sm"
        footer={
          <>
            <Button onClick={() => setAddOpen(false)} disabled={submitting}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleSubmitAdd} disabled={submitting || !form.name.trim()}>
              {submitting ? 'Adding...' : 'Add Company'}
            </Button>
          </>
        }
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <TextField
              label="Company Name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              size="small"
              fullWidth
              required
            />
            {didYouMean && (
              <Typography variant="caption" color="warning.dark" sx={{ mt: 0.5, display: 'block' }}>
                Did you mean{' '}
                <Link
                  component="button"
                  type="button"
                  underline="always"
                  onClick={() => setForm((f) => ({ ...f, name: didYouMean }))}
                  sx={{ fontWeight: 600, verticalAlign: 'baseline' }}
                >
                  {didYouMean}
                </Link>
                ? It&apos;s already on the list.
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              columnGap: 2,
              rowGap: 2,
              alignItems: 'start',
            }}
          >
            <TextField
              select
              label="Tier"
              value={form.tier}
              onChange={(e) => setForm((f) => ({ ...f, tier: e.target.value as TargetCompanyTier }))}
              size="small"
              fullWidth
              helperText="How much the client wants this company."
            >
              {TIER_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Employer Quality"
              value={form.employerQuality}
              onChange={(e) =>
                setForm((f) => ({ ...f, employerQuality: e.target.value as EmployerQuality }))
              }
              size="small"
              fullWidth
            >
              {QUALITY_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label="Why Listed"
              value={form.listReason}
              onChange={(e) =>
                setForm((f) => ({ ...f, listReason: e.target.value as TargetCompanyCategory }))
              }
              size="small"
              fullWidth
              helperText="Why this company is on the list."
            >
              {LIST_REASON_OPTIONS.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Location"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              size="small"
              fullWidth
            />
          </Box>
          <TextField
            label="Why this employer quality"
            value={form.rationale}
            onChange={(e) => setForm((f) => ({ ...f, rationale: e.target.value }))}
            size="small"
            fullWidth
            multiline
            rows={5}
            helperText="Why this company has this employer-quality rating — shown in the detail drawer."
          />
        </Box>
      </DialogShell>
    </Box>
  )
}
